import React, { useState, useRef, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { API_URL } from '../../../utils/apiConfig';

// Decode a canvas data: URL into a File without a network round-trip.
// fetch() on the data: URL reads better but the app's CSP has no `data:` in
// connect-src, so the browser blocks it and the employee sees "Failed to fetch"
// before the signature ever leaves the page.
const dataUrlToFile = (dataUrl, filename) => {
  const [header, base64] = dataUrl.split(',');
  const mimeType = header.match(/:(.*?);/)?.[1] || 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
};

// Separate components remain the same
const OfferDetailsSection = ({ offerDetails }) => (
  <Paper sx={{ p: [2, 3], mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Offer Details
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="textSecondary">Name</Typography>
        <Typography variant="body1">
          {offerDetails.firstName} {offerDetails.lastName}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="textSecondary">Position</Typography>
        <Typography variant="body1">
          {offerDetails.position}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="textSecondary">Store</Typography>
        <Typography variant="body1">
          {offerDetails.storeName}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="textSecondary">Hire Date</Typography>
        <Typography variant="body1">
          {new Date(offerDetails.hireDate).toLocaleDateString()}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="textSecondary">Pay Rate</Typography>
        <Typography variant="body1">
          ${parseFloat(offerDetails.payRate).toFixed(2)} per hour
        </Typography>
      </Grid>
    </Grid>
  </Paper>
);

const PDFViewer = ({ height = '80vh', employeeId, forceRefresh }) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Use centralized API configuration instead of hardcoded URLs
  
  useEffect(() => {
    const fetchPDF = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        
        // Use the simpler direct endpoint with Bearer authentication
        console.log('Fetching offer letter for employee:', employeeId);
        console.log('Using API URL:', API_URL);
        console.log('Force refresh:', forceRefresh);
        
        // Add timestamp to prevent caching
        const timestamp = new Date().getTime();
        const response = await fetch(
          `${API_URL}/employee-documents/offer-letter/${employeeId}?t=${timestamp}&refresh=${forceRefresh}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to load offer letter');
        }
        
        const blob = await response.blob();
        
        // Revoke old blob URL before creating new one
        setPdfBlobUrl(prevUrl => {
          if (prevUrl) {
            URL.revokeObjectURL(prevUrl);
          }
          return null;
        });
        
        // Create new blob URL
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setError(null);
      } catch (err) {
        console.error('Error loading offer letter:', err);
        setError('Failed to load offer letter');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (employeeId) {
      fetchPDF();
    }
    
    // Cleanup
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [employeeId, API_URL, forceRefresh]);
  
  const handleDownload = () => {
    if (pdfBlobUrl) {
      const a = document.createElement('a');
      a.href = pdfBlobUrl;
      a.download = `offer_letter_${employeeId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  
  // Handle expanded view
  if (isExpanded && isMobile) {
    return (
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          p: 1, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Button
            variant="text"
            onClick={() => setIsExpanded(false)}
            sx={{ color: '#007AFF' }}
          >
            Done
          </Button>
          <Button
            variant="text"
            onClick={handleDownload}
            disabled={!pdfBlobUrl}
            startIcon={<span>📥</span>}
          >
            Download
          </Button>
        </Box>
        {pdfBlobUrl && (
          <iframe
            key={`pdf-expanded-${forceRefresh}`}
            src={pdfBlobUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Offer Letter PDF"
          />
        )}
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2 
      }}>
        {isMobile && (
          <Typography variant="body2" color="text.secondary">
            Tap to view full screen
          </Typography>
        )}
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={!pdfBlobUrl}
          startIcon={<span>📥</span>}
          size={isMobile ? "small" : "medium"}
        >
          Download PDF
        </Button>
      </Box>
      
      <Box 
        onClick={() => isMobile && setIsExpanded(true)}
        sx={{ 
          height: isMobile ? '300px' : height, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 1, 
          overflow: 'hidden', 
          mb: 3,
          position: 'relative',
          cursor: isMobile ? 'pointer' : 'default'
        }}>
        {isLoading ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%'
          }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            p: 3
          }}>
            <Typography variant="h6" gutterBottom color="error">
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Refresh Page
            </Button>
          </Box>
        ) : pdfBlobUrl ? (
          <iframe
            key={`pdf-${forceRefresh}`}
            src={pdfBlobUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Offer Letter PDF"
          />
        ) : null}
      </Box>
    </Box>
  );
};

const SignatureSection = ({ 
  firstName,
  setFirstName,
  lastName,
  setLastName,
  legalConsent,
  setLegalConsent,
  formErrors,
  canvasRef,
  signaturePadRef,
  isMobile
}) => (
  <Paper sx={{ p: [2, 3], mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Electronic Signature Confirmation
    </Typography>

    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={formErrors.firstName}
            helperText={formErrors.firstName ? 'Please enter your correct first name' : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={formErrors.lastName}
            helperText={formErrors.lastName ? 'Please enter your correct last name' : ''}
          />
        </Grid>
      </Grid>

      <FormControlLabel
        control={
          <Checkbox
            checked={legalConsent}
            onChange={(e) => setLegalConsent(e.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            I understand that by typing my name and drawing my signature below, I am electronically 
            signing this offer letter. I acknowledge that this electronic signature shall have 
            the same legal effect as a handwritten signature, pursuant to applicable electronic 
            signature laws and regulations. I confirm that the name I have entered matches my legal name
            as it appears on this offer letter.
          </Typography>
        }
        sx={{ mt: 2 }}
      />
      {formErrors.legalConsent && (
        <FormHelperText error>
          You must acknowledge the legal consent to proceed
        </FormHelperText>
      )}
    </Box>

    <Typography variant="h6" gutterBottom>
      Signature
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
      Please sign below to accept the offer letter.
    </Typography>
    <Box 
      sx={{ 
        border: 1, 
        borderColor: formErrors.signature ? 'error.main' : 'grey.300',
        borderRadius: 1,
        mb: 2,
        position: 'relative',
        touchAction: 'none'
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: isMobile ? '150px' : '150px',
          touchAction: 'none'
        }}
      />
    </Box>
    {formErrors.signature && (
      <FormHelperText error sx={{ mb: 1 }}>
        Please provide your signature
      </FormHelperText>
    )}
    <Button
      variant="outlined"
      onClick={() => signaturePadRef.current?.clear()}
      sx={{ mr: 1 }}
    >
      Clear
    </Button>
  </Paper>
);

const OfferLetterForm = ({ 
  employeeId, 
  formId,
  employee,
  isCompleted: initialIsCompleted,
  onSubmit,
  refreshTrigger
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [offerDetails, setOfferDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalConsent, setLegalConsent] = useState(false);
  const [formErrors, setFormErrors] = useState({
    firstName: false,
    lastName: false,
    legalConsent: false,
    signature: false
  });
  
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [forceRefresh, setForceRefresh] = useState(0);


    // Add logging for state changes
    useEffect(() => {
      console.log('🔄 OfferLetterForm State:', {
        initialIsCompleted,
        isCompleted,
        hasOfferDetails: !!offerDetails,
        employeeId,
        formId
      });
    }, [initialIsCompleted, isCompleted, offerDetails, employeeId, formId]);

    // Listen for offer letter updates
    useEffect(() => {
      const handleStorageChange = (e) => {
        if (e.key === 'offerLetterUpdated') {
          const updateData = JSON.parse(e.newValue || '{}');
          if (updateData.employeeId === employeeId) {
            console.log('📢 Offer letter update detected for this employee (storage)');
            setForceRefresh(prev => prev + 1);
            // Clear the completed state to allow re-signing if needed
            setIsCompleted(false);
          }
        }
      };

      const handleCustomEvent = (e) => {
        if (e.detail.employeeId === employeeId) {
          console.log('📢 Offer letter update detected for this employee (custom event)');
          setForceRefresh(prev => prev + 1);
          // Clear the completed state to allow re-signing if needed
          setIsCompleted(false);
        }
      };

      window.addEventListener('storage', handleStorageChange);
      window.addEventListener('offerLetterUpdated', handleCustomEvent);
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('offerLetterUpdated', handleCustomEvent);
      };
    }, [employeeId]);



  useEffect(() => {
    const fetchOfferLetterDetails = async () => {
      console.log('📥 Fetching offer letter details:', {
        employeeId,
        initialIsCompleted,
        refreshTrigger
      });

      const token = localStorage.getItem('token');
      try {
        // Fetch employee details only - PDFViewer handles PDF fetching
        const detailsResponse = await fetch(
          `${API_URL}/employees/${employeeId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!detailsResponse.ok) {
          throw new Error('Failed to fetch employee details.');
        }

        const employeeData = await detailsResponse.json();
        const mappedData = {
          ...employeeData,
          storeName: employeeData.locationName
        };
        setOfferDetails(mappedData);
        console.log('✅ Offer details fetched:', mappedData);

        // Set completion status after successful fetch
        if (initialIsCompleted) {
          console.log('📝 Setting completed state from prop');
          setIsCompleted(true);
        }
      } catch (err) {
        console.error('❌ Error fetching offer letter details:', err);
        setError('Failed to fetch offer letter details.');
      }
    };

    fetchOfferLetterDetails();
  }, [employeeId, initialIsCompleted, refreshTrigger, forceRefresh]);

  // Initialize signature pad
  useEffect(() => {
    let mounted = true;

    const initializeSignaturePad = async () => {
      if (!isCompleted && canvasRef.current && !signaturePadRef.current) {
        try {
          const SignaturePad = (await import('signature_pad')).default;

          // Re-check after the await — see W4Form for the full story. The ref is
          // guarded above but read again here, and the lazily-imported chunk
          // leaves a window in which a re-render can null it.
          if (!mounted || !canvasRef.current) return;

          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const canvas = canvasRef.current;
          const container = canvas.parentElement;
          if (!container) return;
          const containerWidth = container.offsetWidth;
          
          canvas.style.width = '100%';
          canvas.style.height = '150px';
          canvas.width = containerWidth * ratio;
          canvas.height = 150 * ratio;
          
          const context = canvas.getContext('2d');
          context.scale(ratio, ratio);
          
          if (mounted) {
            signaturePadRef.current = new SignaturePad(canvas, {
              backgroundColor: 'rgb(255, 255, 255)',
              penColor: 'rgb(0, 0, 0)',
              velocityFilterWeight: 0.7,
              minWidth: 0.5,
              maxWidth: 2.5,
              throttle: 16,
            });
          }
        } catch (error) {
          console.error('Error initializing signature pad:', error);
        }
      }
    };

    initializeSignaturePad();

    return () => {
      mounted = false;
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
        signaturePadRef.current = null;
      }
    };
  }, [isCompleted]);

  const validateForm = () => {
    const errors = {
      firstName: !firstName.trim() || firstName.trim() !== offerDetails?.firstName,
      lastName: !lastName.trim() || lastName.trim() !== offerDetails?.lastName,
      legalConsent: !legalConsent,
      signature: !signaturePadRef.current || signaturePadRef.current.isEmpty()
    };
    
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please complete all required fields correctly and provide your signature');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const signatureDataUrl = signaturePadRef.current.toDataURL('image/png');
      const signatureFile = dataUrlToFile(signatureDataUrl, 'signature.png');

      const formData = new FormData();
      formData.append('signature', signatureFile);
      formData.append('employeeId', employeeId);
      formData.append('formId', formId);
      formData.append('firstName', firstName);
      formData.append('lastName', lastName);
      formData.append('signedDate', new Date().toISOString());
      formData.append('legalConsent', legalConsent);
      formData.append('isCompleted', 'true');
      formData.append('completedAt', new Date().toISOString());
      formData.append('signedAt', new Date().toISOString());

      const response = await fetch(`${API_URL}/employee-documents/sign-offer-letter`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit signed offer letter');
      }

      // No need to update PDF URL - PDFViewer will handle refreshing

      // Update form completion status
      await fetch(
        `${API_URL}/employee-documents/form-status/${employeeId}/${formId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isCompleted: true,
            completedAt: new Date().toISOString(),
            signedAt: new Date().toISOString()
          })
        }
      );

      setIsCompleted(true);
      if (onSubmit) {
        // Pass both formId and filePath for status update
        // Offer letter doesn't have a separate filePath as it's generated on demand
        onSubmit(formId, null);
      }
    } catch (err) {
      console.error('Error submitting offer letter:', err);
      setError(err.message || 'Failed to submit signed offer letter');
    } finally {
      setIsLoading(false);
    }
  };

  if (isCompleted || initialIsCompleted) {
    return (
      <Box sx={{ maxWidth: 'lg', mx: 'auto', p: [2, 3] }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Offer letter has been signed successfully.
        </Alert>
        {offerDetails && <OfferDetailsSection offerDetails={offerDetails} />}
        <PDFViewer employeeId={employeeId} forceRefresh={forceRefresh} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto', p: [2, 3] }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {offerDetails && <OfferDetailsSection offerDetails={offerDetails} />}
      <PDFViewer employeeId={employeeId} height="60vh" forceRefresh={forceRefresh} />

      <SignatureSection
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        legalConsent={legalConsent}
        setLegalConsent={setLegalConsent}
        formErrors={formErrors}
        canvasRef={canvasRef}
        signaturePadRef={signaturePadRef}
        isMobile={isMobile}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isLoading}
        fullWidth
        size="large"
        sx={{ 
          py: 2,
          fontSize: '1.1rem',
          '&:active': { transform: 'scale(0.98)' }
        }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={24} sx={{ mr: 1.5 }} />
            Processing...
          </>
        ) : (
          'Sign & Submit Offer Letter'
        )}
      </Button>
    </Box>
  );
};

export default OfferLetterForm;